import { createElement, useEffect, useState } from "react";
import { useTranslation } from "../../localization/useTranslation";
import { DotLegalFile, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { post } from "../../common/api/apiShared";
import { _arrayBufferToBase64 } from "../../common/arrayBufferToBase64";
import { Trans } from "react-i18next";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { ICustomizeEmailLogoDialog } from "./CustomizeEmailLogoDialog";
import { EmailLogoCustomizationViewModel } from "./CustomizeEmailLogoDialog.types";
import { useDotLegalQuery, useDotLegalMutation, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";

export default function useCustomizeEmailLogoDialogHooks(props: ICustomizeEmailLogoDialog) {
    const { emailAddress } = useUserContext();
    const { translateString } = useTranslation();

    const snackbar = useDotLegalSnackbar();

    const [error, setError] = useState("");
    const [viewModel, setViewModel] = useState<EmailLogoCustomizationViewModel | undefined>(undefined);
    const [emailPreview, setEmailPreview] = useState<string | undefined>(undefined);

    const queryKey = "EmailLogo";
    const { isLoading, data } = useDotLegalQueryWithKey<EmailLogoCustomizationViewModel>(queryKey, "/email-template/GetLogoId");

    useEffect(() => {
        if (data) {
            setViewModel({ fileName: data.fileName, logoId: data.logoId });
            if (data.emailContent) {
                setEmailPreview(data.emailContent);
            }
        }
    }, [data, isLoading]);

    function validateFile(files: DotLegalFile[]) {
        if (files.length > 1) {
            setError(translateString("oneFileOnly"));
            return false;
        } else if (files.length === 1) {
            if (!files[0].fileName.toLowerCase().endsWith(".png")) {
                setError(translateString("emailLogoWrongFileFormat"));
                return false;
            }
        }

        setError("");
        return true;
    }

    const sendEmailMutation = useDotLegalMutation(sendTestEmailAPI, {
        onSuccess: (response: any) => {
            snackbar.show(createElement(Trans, { i18nKey: "testEmailSent", values: { email: emailAddress } }));
        },
    });

    const onSendEmailClick = async () => {
        await sendEmailMutation.mutateAsync();
    };

    const onSaveLogoClick = async () => {
        await saveMutation.mutateAsync();
    };

    const saveMutation = useDotLegalMutation(saveAPI, {
        onSuccess: (response: any) => {
            snackbar.show(translateString("logoAddedToMails"));
            props.onDialogClose();
        },
    });

    const uploadLogoMutation = useDotLegalMutation(uploadLogoApi);
    const onFileUpload = async (files: Array<DotLegalFile> | undefined) => {
        if (files) {
            const isValid = validateFile(files);

            if (isValid) {
                let tempFile = { ...files[0] };
                await uploadLogoMutation.mutateAsync(await _arrayBufferToBase64(await tempFile.file.arrayBuffer()), {
                    onSuccess: (response: any) => {
                        setViewModel({ fileName: tempFile.fileName, logoId: response.value().logoId });
                        setEmailPreview(response.value().emailContent);
                    },
                });
            }
        }
    };

    const onRemoveFile = () => {
        setViewModel(undefined);
    };

    return {
        onFileUpload,
        error,
        onSendEmailClick,
        onSaveLogoClick,
        isSendingEmail: sendEmailMutation.isLoading,
        isSavingLogo: saveMutation.isLoading,
        isLoading,
        viewModel,
        onRemoveFile,
        emailPreview,
    };

    async function saveAPI() {
        return post<{}>("/email-template/Logo", viewModel);
    }

    async function uploadLogoApi(base64Data: string) {
        return post<{}>("/email-template/uploadLogo", base64Data);
    }

    async function sendTestEmailAPI() {
        return post<{}>("/email-template/SendTestLogoEmail", viewModel?.logoId);
    }
}
